import React, { useState, useMemo, useEffect } from "react";
import { X, FileSpreadsheet, Upload, AlertCircle } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../../lib/utils";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "../../components/ui/dialog";

// Mock validation row datasets for each type
const UOM_IMPORT_PREVIEW = [
  { id: 1, row: 1, name: "Cubic Yard", status: "Valid", reason: "Ready to import" },
  { id: 2, row: 2, name: "Metric Ton", status: "Valid", reason: "Ready to import" },
  { id: 3, row: 3, name: "Kilogram", status: "Skipped", reason: "Name already exists" },
  { id: 4, row: 4, name: "", status: "Error", reason: "Blank Name" },
  { id: 5, row: 5, name: "Drum", status: "Error", reason: "Invalid UQC" },
  { id: 6, row: 6, name: "Gallon", status: "Valid", reason: "Ready to import" },
  { id: 7, row: 7, name: "Barrel", status: "Valid", reason: "Ready to import" },
  { id: 8, row: 8, name: "Pallet", status: "Valid", reason: "Ready to import" },
  { id: 9, row: 9, name: "Centimeter", status: "Valid", reason: "Ready to import" },
  { id: 10, row: 10, name: "Yard", status: "Skipped", reason: "Name already exists" },
  { id: 11, row: 11, name: "Fluid Ounce", status: "Valid", reason: "Ready to import" },
  { id: 12, row: 12, name: "Milligram", status: "Valid", reason: "Ready to import" },
  { id: 13, row: 13, name: "Cubic Yard", status: "Error", reason: "Duplicate Name in file" },
];

const TAX_IMPORT_PREVIEW = [
  { id: 1, row: 1, name: "GST 28%", status: "Skipped", reason: "Name already exists" },
  { id: 2, row: 2, name: "VAT 15%", status: "Valid", reason: "Ready to import" },
  { id: 3, row: 3, name: "GST 3%", status: "Valid", reason: "Ready to import" },
  { id: 4, row: 4, name: "Cess 2%", status: "Valid", reason: "Ready to import" },
  { id: 5, row: 5, name: "Custom 15%", status: "Valid", reason: "Ready to import" },
  { id: 6, row: 6, name: "", status: "Error", reason: "Blank Name" },
  { id: 7, row: 7, name: "GST 18%", status: "Skipped", reason: "Name already exists" },
  { id: 8, row: 8, name: "VAT 12%", status: "Skipped", reason: "Name already exists" },
  { id: 9, row: 9, name: "Service Tax 12.5%", status: "Valid", reason: "Ready to import" },
  { id: 10, row: 10, name: "Surcharge 5%", status: "Valid", reason: "Ready to import" },
  { id: 11, row: 11, name: "GST 120%", status: "Error", reason: "Percentage must be between 0.00 and 100.00" },
  { id: 12, row: 12, name: "GST 18.125%", status: "Error", reason: "Percentage can have at most two decimal places" },
  { id: 13, row: 13, name: "GST 3%", status: "Error", reason: "Duplicate Name in file" },
];

const CURRENCY_IMPORT_PREVIEW = [
  { id: 1, row: 1, name: "US Dollar", status: "Skipped", reason: "Name already exists" },
  { id: 2, row: 2, name: "Euro", status: "Skipped", reason: "Name already exists" },
  { id: 3, row: 3, name: "Japanese Yen", status: "Skipped", reason: "Name already exists" },
  { id: 4, row: 4, name: "Indian Rupee", status: "Skipped", reason: "Name already exists" },
  { id: 5, row: 5, name: "Australian Dollar", status: "Valid", reason: "Ready to import" },
  { id: 6, row: 6, name: "Canadian Dollar", status: "Valid", reason: "Ready to import" },
  { id: 7, row: 7, name: "Swiss Franc", status: "Valid", reason: "Ready to import" },
  { id: 8, row: 8, name: "Singapore Dollar", status: "Valid", reason: "Ready to import" },
  { id: 9, row: 9, name: "UAE Dirham", status: "Valid", reason: "Ready to import" },
  { id: 10, row: 10, name: "", status: "Error", reason: "Blank Name" },
  { id: 11, row: 11, name: "Chinese Yuan", status: "Error", reason: "ISO Code must contain exactly three letters" },
  { id: 12, row: 12, name: "Australian Dollar", status: "Error", reason: "Duplicate Name in file" },
];

// Helper components for validation status badges
function ValidationBadge({ status }) {
  if (status === "Valid") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200/40">
        Valid
      </span>
    );
  }
  if (status === "Skipped") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200/40">
        Skipped
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200/40">
      Error
    </span>
  );
}

export function BulkImportPopup({ open, onOpenChange, type, onImport }) {
  const [fileState, setFileState] = useState(null); // 'valid' | 'empty' | 'wrong' | 'missing' | null
  const [fileName, setFileName] = useState("");
  const [fileSize, setFileSize] = useState("");

  // Reset file states when opening
  useEffect(() => {
    if (open) {
      setFileState(null);
      setFileName("");
      setFileSize("");
    }
  }, [open]);

  // Selected master label
  const masterLabel = useMemo(() => {
    if (type === "uom") return "Unit of Measure";
    if (type === "tax") return "Tax & Duties";
    if (type === "currency") return "Currencies";
    return type || "";
  }, [type]);

  // Selected master columns list
  const templateColumnsLabel = useMemo(() => {
    if (type === "uom") return "Name, Symbol, UQC";
    if (type === "tax") return "Name, Tax Type, Percentage";
    if (type === "currency") return "Name, ISO Code, Symbol, Base Currency";
    return "Name, Abbreviation, Description";
  }, [type]);

  // Retrieve dataset matching selected field
  const previewRows = useMemo(() => {
    if (fileState !== "valid") return [];
    if (type === "uom") return UOM_IMPORT_PREVIEW;
    if (type === "tax") return TAX_IMPORT_PREVIEW;
    if (type === "currency") return CURRENCY_IMPORT_PREVIEW;
    return [];
  }, [fileState, type]);

  // Compute status counts for valid rows
  const stats = useMemo(() => {
    if (fileState !== "valid") return { valid: 0, skipped: 0, error: 0 };
    const valid = previewRows.filter((r) => r.status === "Valid").length;
    const skipped = previewRows.filter((r) => r.status === "Skipped").length;
    const error = previewRows.filter((r) => r.status === "Error").length;
    return { valid, skipped, error };
  }, [fileState, previewRows]);

  const handleSimulate = (state) => {
    setFileName(`Master_${masterLabel.replace(/\s+/g, "")}.xlsx`);
    setFileSize("28 KB");
    setFileState(state);
  };

  const handleRemoveFile = () => {
    setFileState(null);
    setFileName("");
    setFileSize("");
  };

  const handleImport = () => {
    if (stats.valid > 0) {
      const validItems = previewRows.filter((r) => r.status === "Valid");
      onImport(validItems);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        {/* Overlay backdrop */}
        <DialogOverlay className="bg-[#0a1230]/40 backdrop-blur-sm fixed inset-0 z-40 transition-opacity animate-in fade-in duration-200" />

        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] sm:w-[90vw] md:w-[700px]",
            "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
            "max-h-[90vh] flex flex-col justify-between font-sans focus:outline-none",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--color-text-border)]/70 shrink-0">
            <div>
              <DialogTitle className="text-lg font-bold text-foreground leading-tight">
                Bulk Import from Excel
              </DialogTitle>
              <p className="text-xs text-[var(--color-text-secondary)] mt-1.5 font-medium">
                Importing into <span className="font-semibold text-foreground">{masterLabel}</span> — the file cannot target another field.
              </p>
            </div>
            <DialogPrimitive.Close className="h-9 w-9 grid place-items-center rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-workspace)]/60 hover:text-[var(--color-primary)] transition focus:outline-none">
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </div>

          {/* Scrollable drawer body */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {/* Section 1 – Download Template Card */}
            <div className="rounded-2xl border border-[var(--color-text-border)]/75 bg-[#EEF2F6]/55 p-4 flex items-center justify-between gap-4 shadow-sm">
              <div className="flex items-center gap-3.5 flex-1">
                <div className="h-10 w-10 shrink-0 rounded-xl bg-indigo-50 text-[var(--color-primary)] grid place-items-center">
                  <FileSpreadsheet className="h-5 w-5" />
                </div>
                <p className="text-xs text-[var(--color-text-secondary)] leading-relaxed font-semibold">
                  Start from the template so the columns are in the exact order the import expects:{" "}
                  <strong className="text-foreground">{templateColumnsLabel}</strong>.
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleSimulate("valid")}
                className="h-9 px-4 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold text-foreground hover:bg-gray-50 transition shadow-sm cursor-pointer whitespace-nowrap"
              >
                Download Template
              </button>
            </div>

            {/* Section 2 – Upload Excel File Area */}
            {!fileState ? (
              /* Drag-and-drop placeholder */
              <div className="space-y-4">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSimulate("valid")}
                  className="rounded-2xl border-dashed border-2 border-blue-400 bg-indigo-50/15 p-10 flex flex-col items-center justify-center text-center cursor-pointer hover:bg-indigo-50/25 transition shadow-sm"
                >
                  <div className="h-11 w-11 rounded-full bg-[var(--color-primary)]/5 text-[var(--color-primary)] grid place-items-center mb-3">
                    <Upload className="h-5 w-5" />
                  </div>
                  <p className="text-sm font-bold text-foreground leading-tight">
                    Drop your .xlsx here, or click to choose a file
                  </p>
                  <p className="text-xs text-[var(--color-text-secondary)] mt-2.5 leading-normal max-w-xs font-medium">
                    One file at a time. Nothing is written until you review the validation preview and confirm.
                  </p>
                </div>

                {/* Simulation Control links */}
                {/* <div className="text-center text-xs text-[var(--color-text-secondary)] space-y-2">
                  <p className="font-semibold">
                    No file to hand?{" "}
                    <button
                      type="button"
                      onClick={() => handleSimulate("valid")}
                      className="text-[var(--color-primary)] hover:underline font-bold"
                    >
                      Run a sample file for {masterLabel}
                    </button>
                  </p>
                  <div className="text-[10px] text-[var(--color-text-secondary)]/70 flex flex-wrap justify-center gap-1.5 font-bold">
                    <span>Simulate alternative states:</span>
                    <button type="button" onClick={() => handleSimulate("empty")} className="hover:underline text-[var(--color-primary)]/80">Empty File</button>
                    <span>•</span>
                    <button type="button" onClick={() => handleSimulate("wrong")} className="hover:underline text-[var(--color-primary)]/80">Wrong Template</button>
                    <span>•</span>
                    <button type="button" onClick={() => handleSimulate("missing")} className="hover:underline text-[var(--color-primary)]/80">Missing Columns</button>
                  </div>
                </div> */}
              </div>
            ) : (
              /* File selected state indicator card */
              <div className="rounded-2xl border border-[var(--color-text-border)] bg-gray-50/50 p-4 flex items-center justify-between shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-600 grid place-items-center">
                    <FileSpreadsheet className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="text-sm font-bold text-foreground block leading-tight">
                      {fileName}
                    </span>
                    <span className="text-xs text-[var(--color-text-secondary)] mt-1.5 block font-medium">
                      {fileSize} •{" "}
                      {fileState === "valid" ? (
                        <span className="text-emerald-600 font-semibold flex items-center gap-1 inline-flex">
                          ✓ File validation check passed
                        </span>
                      ) : (
                        <span className="text-[var(--color-error)] font-semibold flex items-center gap-1 inline-flex">
                          ⚠ File contains validation issues
                        </span>
                      )}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  className="h-8 w-8 rounded-lg border border-[var(--color-text-border)] bg-white text-[var(--color-text-secondary)] hover:text-foreground hover:bg-gray-100 grid place-items-center transition cursor-pointer"
                  title="Remove file"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* Error banners based on simulated file validation errors */}
            {fileState === "wrong" && (
              <div className="p-4 bg-[var(--color-error)]/8 border border-[var(--color-error)]/25 text-[var(--color-error)] rounded-xl text-xs sm:text-sm font-semibold flex items-start gap-2.5 animate-in fade-in duration-200">
                <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold leading-normal">The uploaded file does not match the required template.</p>
                  <p className="font-normal text-xs text-[var(--color-error)]/90 mt-1">Please download the latest template and try again.</p>
                </div>
              </div>
            )}

            {fileState === "empty" && (
              <div className="p-4 bg-[var(--color-error)]/8 border border-[var(--color-error)]/25 text-[var(--color-error)] rounded-xl text-xs sm:text-sm font-semibold flex items-start gap-2.5 animate-in fade-in duration-200">
                <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold leading-normal">The uploaded file contains no data.</p>
                  <p className="font-normal text-xs text-[var(--color-error)]/90 mt-1">Please add rows to your Excel sheet and try again.</p>
                </div>
              </div>
            )}

            {fileState === "missing" && (
              <div className="p-4 bg-[var(--color-error)]/8 border border-[var(--color-error)]/25 text-[var(--color-error)] rounded-xl text-xs sm:text-sm font-semibold flex items-start gap-2.5 animate-in fade-in duration-200">
                <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold leading-normal">Required columns are missing.</p>
                  <p className="font-normal text-xs text-[var(--color-error)]/90 mt-1">Please download the latest template and ensure required columns are present.</p>
                </div>
              </div>
            )}

            {/* Section 3 - Validation Preview (only shown for valid simulated state) */}
            {fileState === "valid" && (
              <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <h3 className="text-sm font-bold text-foreground">Validation Preview</h3>

                {/* Summary badges card */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-emerald-50/50 border border-emerald-200/60 rounded-xl p-3 text-center transition-shadow hover:shadow-sm">
                    <div className="text-xl font-bold text-emerald-600">{stats.valid}</div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700/80 mt-1 font-sans">Valid</div>
                  </div>
                  <div className="bg-amber-50/50 border border-amber-200/60 rounded-xl p-3 text-center transition-shadow hover:shadow-sm">
                    <div className="text-xl font-bold text-amber-600">{stats.skipped}</div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700/80 mt-1 font-sans">Skipped</div>
                  </div>
                  <div className="bg-rose-50/50 border border-rose-200/60 rounded-xl p-3 text-center transition-shadow hover:shadow-sm">
                    <div className="text-xl font-bold text-rose-600">{stats.error}</div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-rose-700/80 mt-1 font-sans">Error</div>
                  </div>
                </div>

                {/* Preview Table */}
                <div className="rounded-xl border border-[var(--color-text-border)] overflow-hidden bg-white shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs sm:text-sm text-left">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-[var(--color-text-secondary)] bg-[var(--color-workspace)]/30 border-b border-[var(--color-text-border)]/60">
                          <th className="px-4 py-2.5 font-bold">Row</th>
                          <th className="px-4 py-2.5 font-bold">Name</th>
                          <th className="px-4 py-2.5 font-bold">Status</th>
                          <th className="px-4 py-2.5 font-bold">Reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--color-text-border)]/35 font-medium">
                        {previewRows.map((row) => (
                          <tr key={row.id} className="hover:bg-[var(--color-workspace)]/10 transition-colors">
                            <td className="px-4 py-2.5 text-[var(--color-text-secondary)]">{row.row}</td>
                            <td className="px-4 py-2.5 font-semibold text-foreground">
                              {row.name === "" ? <span className="text-[var(--color-error)] font-normal italic">‹empty›</span> : row.name}
                            </td>
                            <td className="px-4 py-2.5">
                              <ValidationBadge status={row.status} />
                            </td>
                            <td className="px-4 py-2.5 text-[var(--color-text-secondary)]">{row.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Fixed Footer */}
          <div className="border-t border-[var(--color-text-border)]/70 px-6 py-4 bg-white flex items-center justify-between shrink-0 rounded-b-2xl">
            <span className="text-[11px] text-[var(--color-text-secondary)] font-medium hidden sm:inline">
              {/* Nothing is written until you confirm the preview. */}
            </span>
            <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="h-10 px-5 rounded-xl border border-[var(--color-text-border)] text-[var(--color-text-secondary)] font-semibold text-sm hover:bg-[var(--color-workspace)]/40 hover:text-[var(--color-primary)] transition shadow-sm cursor-pointer bg-white"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={fileState !== "valid" || stats.valid === 0}
                onClick={handleImport}
                className="h-10 px-6 rounded-xl bg-[var(--color-primary)] text-white font-semibold text-sm hover:bg-[var(--color-accent)] transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {fileState === "valid" ? `Import ${stats.valid} Valid Rows` : "Import Valid Rows"}
              </button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
